import React, { useState } from "react";
import { Dropdown } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";

import PageTitle from "../../layouts/PageTitle";
import ExcelDownloadButton from "../../components/ExcelDownloadButton";
import {
  useGetExpensesQuery,
  useGetVehiclesQuery,
} from "../../../store/appApi";

// Shows all finance entries where isVehicleExpense === true.
// Optionally filtered further by a specific vehicle via the vehicle dropdown.

const VehicleExpenses = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState(20);
  const [filterVehicle, setFilterVehicle] = useState(""); // vehicleId filter
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");

  // Only pull entries flagged as vehicle expenses
  const {
    data: expenseData,
    isLoading,
    isFetching,
  } = useGetExpensesQuery({
    page,
    isVehicleExpense: "true",
    dateFrom: filterDateFrom,
    dateTo: filterDateTo,
  });

  // Vehicle list for the filter dropdown + row label lookup.
  // Pull a large page so every referenced vehicle resolves to a name
  // (default limit is 10, which left most rows showing a raw ObjectId).
  const { data: vehicleData } = useGetVehiclesQuery({ limit: 1000 });
  const vehicles = Array.isArray(vehicleData?.data) ? vehicleData.data : [];

  const allExpenses = Array.isArray(expenseData?.finance)
    ? expenseData.finance
    : [];

  // Client-side filter by vehicleId (backend doesn't expose vehicleId filter yet)
  const expenses = filterVehicle
    ? allExpenses.filter(
        (e) =>
          e.vehicleId === filterVehicle ||
          e.vehicleId?.toString() === filterVehicle,
      )
    : allExpenses;

  const total = Number(expenseData?.total) || 0;
  const totalPages = Math.max(1, Math.ceil(total / sort));
  const showLoading = isLoading || isFetching;
  const showingFrom = total > 0 ? (page - 1) * sort + 1 : 0;
  const showingTo = total > 0 ? Math.min(page * sort, total) : 0;
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { dateStyle: "medium" }) : "—";

  const formatAmount = (n) =>
    n !== undefined && n !== null ? Number(n).toLocaleString("en-IN") : "—";

  const vehicleLabel = (id) => {
    if (!id) return "—";
    const v = vehicles.find((v) => (v._id || v.id) === id.toString());
    if (!v) return "Unknown vehicle";
    return (
      [v.vehicleMake, v.vehicleNumber].filter(Boolean).join(" — ") || "—"
    );
  };

  return (
    <>
      <PageTitle
        activeMenu="Vehicle Expenses"
        motherMenu="Vehicles / Machines"
      />

      <div className="row">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-header d-flex flex-wrap gap-2 align-items-center">
              <h4 className="card-title mb-0">Vehicle / Machine Expenses</h4>
              <div className="ms-auto d-flex gap-2 align-items-center">
                <ExcelDownloadButton
                  endpoint="/api/finance/vehicle-expenses/export"
                  params={{
                    vehicleId:   filterVehicle,
                    dateFrom:    filterDateFrom,
                    dateTo:      filterDateTo,
                  }}
                  title={
                    filterVehicle
                      ? `Vehicle Expenses — ${vehicleLabel(filterVehicle)}`
                      : "Vehicle Expenses"
                  }
                  filename={
                    filterVehicle
                      ? `vehicle-expenses-${vehicleLabel(filterVehicle).replace(/\s/g, "-")}.xlsx`
                      : "vehicle-expenses.xlsx"
                  }
                  label="Export Excel"
                />
                <Link to="/add/expense" className="btn btn-primary btn-sm">
                  + Book Expense
                </Link>
              </div>
            </div>

            {/* Filters */}
            <div className="card-body border-bottom pb-3">
              <div className="row g-2 align-items-end">
                <div className="col-sm-4 col-md-3">
                  <label className="form-label mb-1 small">Vehicle</label>
                  <select
                    className="form-control form-control-sm"
                    value={filterVehicle}
                    onChange={(e) => {
                      setFilterVehicle(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">All Vehicles</option>
                    {vehicles.map((v) => (
                      <option key={v._id || v.id} value={v._id || v.id}>
                        {v.vehicleMake} — {v.vehicleNumber}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-sm-4 col-md-3">
                  <label className="form-label mb-1 small">From</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={filterDateFrom}
                    onChange={(e) => {
                      setFilterDateFrom(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>

                <div className="col-sm-4 col-md-3">
                  <label className="form-label mb-1 small">To</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={filterDateTo}
                    onChange={(e) => {
                      setFilterDateTo(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>

                <div className="col-auto">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => {
                      setFilterVehicle("");
                      setFilterDateFrom("");
                      setFilterDateTo("");
                      setPage(1);
                    }}
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="card-body">
              <div className="table-responsive">
                <div className="dataTables_wrapper no-footer">
                  <div className="dataTables_length mb-3">
                    <label className="d-flex align-items-center gap-2">
                      Show
                      <Dropdown>
                        <Dropdown.Toggle as="div" className="search-drop-btn">
                          {sort}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          {[10, 20, 50].map((n) => (
                            <Dropdown.Item
                              key={n}
                              onClick={() => {
                                setSort(n);
                                setPage(1);
                              }}
                            >
                              {n}
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                      entries
                    </label>
                  </div>

                  <table className="display dataTable no-footer w-100">
                    <thead>
                      <tr>
                        {/* <th>#</th> */}
                        <th>Date</th>
                        <th>Vehicle</th>
                        <th>Category</th>
                        <th>Amount (₹)</th>
                        {/* <th>Balance (₹)</th> */}
                        <th>Posted By</th>
                        <th>Description</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {showLoading && (
                        <tr>
                          <td colSpan={9} className="text-center py-4">
                            <span className="spinner-border spinner-border-sm me-2" />
                            Loading…
                          </td>
                        </tr>
                      )}
                      {!showLoading && expenses.length === 0 && (
                        <tr>
                          <td
                            colSpan={9}
                            className="text-center py-4 text-muted"
                          >
                            No vehicle expenses found for the selected filters.
                          </td>
                        </tr>
                      )}
                      {expenses.map((row, idx) => (
                        <tr key={row._id || idx}>
                          {/* <td>{showingFrom + idx}</td> */}
                          <td>{formatDate(row.created)}</td>
                          <td>
                            <span
                              className="d-inline-flex align-items-start gap-2"
                              style={{ maxWidth: 220 }}
                            >
                              <i
                                className="fa fa-truck text-info mt-1"
                                style={{ fontSize: 12, flexShrink: 0 }}
                              />
                              <span
                                className="fw-semibold text-uppercase"
                                style={{
                                  whiteSpace: "normal",
                                  wordBreak: "break-word",
                                  lineHeight: 1.3,
                                  fontSize: 12,
                                }}
                              >
                                {vehicleLabel(row.vehicleId)}
                              </span>
                            </span>
                          </td>
                          <td style={{ wordBreak: "break-word" }}>
                            {row.category?.name || "—"}
                          </td>
                          <td className="fw-semibold text-danger">
                            ₹{formatAmount(row.amount)}
                          </td>
                          {/* <td>₹{formatAmount(row.balance)}</td> */}
                          <td style={{ textTransform: "capitalize" }}>
                            {row.user?.name || "—"}
                          </td>
                          <td className="text-muted small">
                            {row.desc || "—"}
                          </td>
                          <td>
                            {row.vehicleId && (
                              <button
                                type="button"
                                className="btn btn-xs btn-outline-primary"
                                title="View vehicle detail"
                                onClick={() =>
                                  navigate(
                                    `/payments/finance/vehicle-detail/${row.vehicleId}`,
                                  )
                                }
                              >
                                <i className="fa fa-bar-chart me-1" />
                                View Detail
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Pagination */}
                  <div className="d-sm-flex text-center justify-content-between align-items-center mt-3">
                    <div className="dataTables_info">
                      Showing {showingFrom} to {showingTo} of {total} entries
                    </div>
                    <div className="dataTables_paginate paging_simple_numbers">
                      <Link
                        className={`paginate_button previous ${page <= 1 ? "disabled" : ""}`}
                        to="#"
                        onClick={() => page > 1 && setPage(page - 1)}
                      >
                        Previous
                      </Link>
                      <span>
                        {pageNumbers.map((n) => (
                          <Link
                            key={n}
                            to="#"
                            className={`paginate_button ${page === n ? "current" : ""}`}
                            onClick={() => setPage(n)}
                          >
                            {n}
                          </Link>
                        ))}
                      </span>
                      <Link
                        className={`paginate_button next ${page >= totalPages ? "disabled" : ""}`}
                        to="#"
                        onClick={() => page < totalPages && setPage(page + 1)}
                      >
                        Next
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default VehicleExpenses;
